import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Box,
    Typography,
    Button,
    Chip,
    Alert,
    CircularProgress,
    Tooltip,
    TextField,
    InputAdornment,
    Tabs,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Paper,
    IconButton,
    useMediaQuery,
    useTheme,
    Card,
    CardContent,
    Stack,
    Divider,
    Collapse,
    Switch,
    FormControlLabel,
} from '@mui/material';
import FilterDropdown from '../components/common/FilterDropdown';
import ComponentStockChips from '../components/bundle-category/ComponentStockChips';
import TablePaginationBar, { DEFAULT_PAGE_SIZE } from '../components/common/TablePaginationBar';
import PageHeader from '../components/common/PageHeader';
import PageContainer from '../components/common/PageContainer';
import { TABLE_BORDER, tableContainerSx, tableHeadRowSx, tableBodySx } from '../components/common/tableStyles';
import DocRangeFilter, { DocRangeValue, EMPTY_DOC_RANGE, isDocRangeActive, docRangeParams } from '../components/common/DocRangeFilter';
import CogsRangeFilter, { CogsRangeValue, EMPTY_COGS_RANGE, isCogsRangeActive, cogsRangeParams } from '../components/common/CogsRangeFilter';
import { useToast } from '../components/common/useToast';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import {
    Search,
    Download,
    Refresh,
    Upload,
    Add,
    Inventory2,
    Extension,
    Edit,
    ChevronRight,
    Category as CategoryIcon,
    InfoOutlined,
    History,
    ExpandMore,
    MonitorHeart,
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { WarehouseAPI } from '../services/api';
import { BundleCategoryRow, ComponentViewRow } from '../types';
import { docColorBundle, docColorComponent } from '../utils/docStatus';
import { usePermission } from '../hooks/usePermission';
import { ImageThumb, ImageLightbox, LightboxSection } from '../components/carding/CardingImageComponents';
import BundleCategoryDetail from '../components/bundle-category/BundleCategoryDetail';
import BundleCategoryManager, { BundleCategoryManagerHandle } from './BundleCategoryManager';
import DataHealthTab from '../components/bundle-category/DataHealthTab';
import { BundleCogs, formatInr, formatAsOfMonth, missingSkusLabel } from '../utils/cogsFormat';
import { csvCell } from '../utils/csv';
import { CogsShareBar, cogsSegmentColor } from '../components/common/CogsVisuals';
import { tokens } from '../theme/tokens';

// ── Constants ─────────────────────────────────────────────────────────────────
const PAGE_SIZE = DEFAULT_PAGE_SIZE;

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatNumber = (n: number | null | undefined): string => {
    if (n === null || n === undefined || n === Infinity || isNaN(n as number)) return '—';
    return n.toLocaleString('en-IN');
};

const formatDecimal = (n: number | null | undefined): string => {
    if (n === null || n === undefined || n === Infinity || isNaN(n as number)) return '—';
    return n.toFixed(1);
};

// ── COGS (post-GST) ───────────────────────────────────────────────────────────
// BundleCogs type + formatInr/formatAsOfMonth/missingSkusLabel now live in utils/cogsFormat so the
// cards and the parent detail view render COGS identically. See that file for the post-GST basis.

const CogsTotalCell: React.FC<{ cogs?: BundleCogs }> = ({ cogs }) => {
    if (!cogs) return <Typography component="span" sx={{ color: 'text.disabled' }}>—</Typography>;
    if (cogs.total === null) {
        return (
            <Tooltip title={cogs.missingSkus.length > 0 ? `No COGS found for: ${missingSkusLabel(cogs.missingSkus)}` : 'No COGS found'}>
                <Chip label="No cost data" size="small" sx={{ height: 20, fontSize: '0.64rem', fontWeight: 700, bgcolor: 'rgba(100,116,139,0.14)', color: '#4A433D' }} />
            </Tooltip>
        );
    }
    // Frozen/as-of totals are sealed to a month — surface an "as of <Mon YYYY>" chip so a frozen
    // post-GST number is never mistaken for the live one. A version created before COGS coverage
    // began (post_gst_snapshot_earliest) reads "as of Jun 2026 · earliest available". Live rows
    // (post_gst_live, no asOf) show nothing extra.
    const asOfMonth = formatAsOfMonth(cogs.asOf);
    const isEarliest = cogs.costBasis === 'post_gst_snapshot_earliest';
    const showAsOf = cogs.frozen === true || !!asOfMonth || isEarliest;
    const chipLabel = isEarliest
        ? `as of ${asOfMonth || 'Jun 2026'} · earliest available`
        : asOfMonth ? `as of ${asOfMonth}` : 'frozen';
    const asOfChip = showAsOf ? (
        <Chip
            label={chipLabel}
            size="small"
            sx={{ height: 18, fontSize: '0.6rem', fontWeight: 800, bgcolor: 'rgba(100,116,139,0.14)', color: '#4A433D', cursor: isEarliest ? 'help' : 'default' }}
        />
    ) : null;
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.4 }}>
            <Typography component="span" sx={{ fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums', fontWeight: 800, fontSize: '0.92rem', color: tokens.ink, letterSpacing: '-0.01em' }}>{formatInr(cogs.total)}</Typography>
            {cogs.partial && (
                <Tooltip title={`${cogs.costedCount} of ${cogs.componentCount} components costed. Missing: ${missingSkusLabel(cogs.missingSkus)}`}>
                    <Typography component="span" sx={{ fontSize: '0.62rem', fontWeight: 700, color: tokens.muted, cursor: 'help', fontVariantNumeric: 'tabular-nums' }}>
                        {cogs.costedCount}/{cogs.componentCount} costed
                    </Typography>
                </Tooltip>
            )}
            {asOfChip && (isEarliest ? (
                <Tooltip title="Cost as of the earliest available snapshot (Jun 2026) — this version predates COGS coverage, so the first available post-GST cost is shown.">
                    {asOfChip}
                </Tooltip>
            ) : asOfChip)}
        </Box>
    );
};

// DOC semantic colour scale moved to utils/docStatus (shared with the Bundle Catalog detail view).

// ── Sub-components ────────────────────────────────────────────────────────────

/** Shows a list of chips. When items exceed `max`, renders a +N toggle that expands inline. */
// Visual encoding for the Packaging/Add-On/SKU/Blank classification shown on component
// chips throughout Bundle Categories, Simple/Bundle view, and Component View — kept as
// one shared lookup so the color/icon meaning stays consistent across tables.
const COMPONENT_TYPE_STYLE: Record<'PACKAGING' | 'ADD_ON' | 'SKU' | 'BLANK', { icon: string; sx: object }> = {
    PACKAGING: { icon: '📦', sx: { borderColor: '#d97706', color: '#92400e', bgcolor: '#fffbeb' } },
    ADD_ON: { icon: '➕', sx: { borderColor: '#B8451E', color: '#B8451E', bgcolor: 'rgba(220, 88, 41, 0.06)' } },
    SKU: { icon: '🏷️', sx: { borderColor: '#6B625B', color: '#4A433D', bgcolor: '#FAFAF7' } },
    // Deliberately loud (not a muted "missing data" gray) — this means Redshift has no
    // category signal at all for this SKU, which someone needs to go fix at the source.
    BLANK: { icon: '⚠️', sx: { borderColor: '#dc2626', color: '#fff', bgcolor: '#dc2626', fontWeight: 700 } },
};

// One distinct color per real Product Category value (Jewelry, Rakhi, Gift Box, ...), not
// just the 3-way Packaging/Add-On/SKU bucket. The category list is dynamic (fetched from
// the DB), so colors are generated rather than hand-mapped by name — golden-angle hue
// spacing (137.508°) guarantees every category gets a hue far from its neighbors no matter
// how many exist, so no two categories ever collide. Packaging/Add On keep their existing
// fixed colors for continuity with the bucket-based coloring used elsewhere.
const FIXED_CATEGORY_SX: Record<string, object> = {
    'Packaging': COMPONENT_TYPE_STYLE.PACKAGING.sx,
    'Add On': COMPONENT_TYPE_STYLE.ADD_ON.sx,
};
function hueChipSx(hue: number): object {
    return {
        borderColor: `hsl(${hue}, 65%, 42%)`,
        color: `hsl(${hue}, 70%, 30%)`,
        bgcolor: `hsl(${hue}, 70%, 96%)`,
    };
}
function buildCategoryColorMap(categories: string[]): Map<string, object> {
    const map = new Map<string, object>();
    const dynamic = categories.filter(c => !FIXED_CATEGORY_SX[c]).sort();
    dynamic.forEach((cat, i) => map.set(cat, hueChipSx((i * 137.508) % 360)));
    Object.entries(FIXED_CATEGORY_SX).forEach(([cat, sx]) => map.set(cat, sx));
    return map;
}

const CollapsedChipList: React.FC<{
    items: string[];
    max?: number;
    chipLabel?: (item: string) => string;
    chipSx?: (item: string) => object;
    color?: 'default' | 'primary' | 'secondary';
    expanded?: boolean;
    onToggle?: () => void;
}> = ({ items, max = 3, chipLabel, chipSx, color = 'default', expanded = false, onToggle }) => {
    const showAll = expanded || items.length <= max;
    const displayItems = showAll ? items : items.slice(0, max);
    const remaining = items.length - max;

    if (items.length === 0) return <Typography variant="caption" color="text.disabled">—</Typography>;

    return (
        <Box sx={{ display: 'flex', gap: 0.3, flexWrap: 'wrap', alignItems: 'center' }}>
            {displayItems.map((item) => (
                <Chip
                    key={item}
                    label={chipLabel ? chipLabel(item) : item}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.65rem', height: 20, ...(chipSx ? chipSx(item) : {}) }}
                />
            ))}
            {!showAll && onToggle && (
                <Chip
                    label={`+${remaining}`}
                    size="small"
                    color="secondary"
                    variant="outlined"
                    sx={{ fontSize: '0.65rem', height: 20, cursor: 'pointer', fontWeight: 700 }}
                    onClick={(e) => { e.stopPropagation(); onToggle(); }}
                />
            )}
            {showAll && items.length > max && onToggle && (
                <Chip
                    label="− less"
                    size="small"
                    color="default"
                    variant="outlined"
                    sx={{ fontSize: '0.65rem', height: 20, cursor: 'pointer', fontWeight: 700, borderStyle: 'dashed' }}
                    onClick={(e) => { e.stopPropagation(); onToggle(); }}
                />
            )}
        </Box>
    );
};

// ── Pagination Bar ───────────────────────────────────────────────────────────

const PaginationBar = TablePaginationBar;

// ── Mobile card view — Simple/Bundle view ────────────────────────────────────
// The desktop table (13+ columns) doesn't translate to a phone screen no matter how it's
// styled; below the `md` breakpoint this renders one card per bundle instead. Leads with
// identity (SKU ID, Status, Type) matching how the desktop table itself leads, with DOC/
// DRR/Inventory/Components behind a tap-to-expand section so the card stays scannable.
const BundleMobileCard: React.FC<{
    cat: BundleCategoryRow;
    onRowClick: (parentSKU: string) => void;
    onImageClick: (title: string, images: string[]) => void;
}> = ({ cat, onRowClick, onImageClick }) => {
    const [expanded, setExpanded] = useState(false);
    const b = cat.activeBundle!;

    const type = (b as any).saleType === 'Simple' ? 'Simple' : 'Bundle';
    const physicalDoc = (b as any).physicalDoc !== undefined
        ? (b as any).physicalDoc as number | null
        : (b.drr && b.drr > 0 && b.inventory ? b.inventory / b.drr : null);
    const potentialDoc = (b as any).potentialDoc !== undefined
        ? (b as any).potentialDoc as number | null
        : (b.drr && b.drr > 0 && b.canBeMade ? b.canBeMade / b.drr : null);
    const componentsList: Array<{ sku: string; isPackaging: boolean; componentType?: 'PACKAGING' | 'ADD_ON' | 'SKU'; inventory?: number; isLimiting?: boolean }> = (b as any).componentsList !== undefined
        ? (b as any).componentsList as Array<{ sku: string; isPackaging: boolean; componentType?: 'PACKAGING' | 'ADD_ON' | 'SKU'; inventory?: number; isLimiting?: boolean }>
        : (b.childs || []).map((sku: string) => ({ sku, isPackaging: false, componentType: 'SKU' as const }));
    const packagingSku = (b as any).packagingSku;
    const images = b.images && b.images.length > 0 ? b.images : (b.imageURL && b.imageURL.startsWith('http') ? [b.imageURL] : []);

    return (
        <Card variant="outlined" sx={{ mb: 1.5 }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }} onClick={() => onRowClick(cat.parentSKU)}>
                    <ImageThumb
                        images={images}
                        label={b.bundleSku}
                        onClick={(e) => { e?.stopPropagation(); if (images.length > 0) onImageClick(b.bundleSku, images); }}
                        size={44}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                            <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.85rem' }}>{b.bundleSku}</Typography>
                            <Chip
                                label={(b as any).isActive === false ? 'Inactive' : 'Active'}
                                size="small"
                                color={(b as any).isActive === false ? 'default' : 'success'}
                                variant={(b as any).isActive === false ? 'outlined' : 'filled'}
                                sx={{ fontWeight: 600, fontSize: '0.65rem', height: 20 }}
                            />
                            <Chip
                                label={type}
                                size="small"
                                color={type === 'Simple' ? 'primary' : 'default'}
                                variant="outlined"
                                sx={{ fontWeight: 600, fontSize: '0.65rem', height: 20 }}
                            />
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                            {cat.parentSKU} · {b.name || '—'}
                        </Typography>
                    </Box>
                    <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); setExpanded(x => !x); }}
                        sx={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
                    >
                        <ExpandMore fontSize="small" />
                    </IconButton>
                </Box>

                <Collapse in={expanded}>
                    <Divider sx={{ my: 1.25 }} />
                    <Stack spacing={1}>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Chip size="small" label={`Physical DOC: ${formatDecimal(physicalDoc)}`} color={docColorBundle(physicalDoc)} variant={physicalDoc !== null ? 'filled' : 'outlined'} sx={{ fontWeight: 600 }} />
                            <Chip size="small" label={`Potential DOC: ${formatDecimal(potentialDoc)}`} color={docColorBundle(potentialDoc)} variant={potentialDoc !== null ? 'filled' : 'outlined'} sx={{ fontWeight: 600 }} />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Typography variant="caption">DRR: <strong>{formatDecimal(b.drr)}</strong></Typography>
                            <Typography variant="caption">Inventory: <strong>{formatNumber(b.inventory)}</strong></Typography>
                            <Typography variant="caption">Can Be Made: <strong>{formatNumber(b.canBeMade)}</strong></Typography>
                            {(b as any).cogs && (b as any).cogs.total !== null && (() => {
                                const c = (b as any).cogs as BundleCogs;
                                const asOfMonth = formatAsOfMonth(c?.asOf);
                                const isEarliest = c?.costBasis === 'post_gst_snapshot_earliest';
                                const showAsOf = c?.frozen === true || !!asOfMonth || isEarliest;
                                const asOfText = showAsOf
                                    ? ` · ${isEarliest ? `as of ${asOfMonth || 'Jun 2026'} · earliest available` : asOfMonth ? `as of ${asOfMonth}` : 'frozen'}`
                                    : '';
                                const caption = <Typography variant="caption">COGS: <strong>{formatInr(c?.total)}</strong>{c?.partial ? ' (partial)' : ''}{asOfText}</Typography>;
                                return showAsOf && isEarliest ? (
                                    <Tooltip title="Cost as of the earliest available snapshot (Jun 2026) — this version predates COGS coverage.">{caption}</Tooltip>
                                ) : caption;
                            })()}
                            <Typography variant="caption">Packaging: <strong>{packagingSku || (packagingSku === null ? 'none' : '—')}</strong></Typography>
                        </Box>
                        {(['SKU', 'ADD_ON', 'PACKAGING'] as const).map(typeKey => {
                            const items = componentsList.filter(c => (c.componentType || 'SKU') === typeKey);
                            if (items.length === 0) return null;
                            return (
                                <Box key={typeKey}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.3 }}>
                                        {typeKey === 'SKU' ? 'SKU Components' : typeKey === 'ADD_ON' ? 'Add-Ons' : 'Packaging Components'}
                                    </Typography>
                                    <ComponentStockChips items={items} max={99} chipSx={COMPONENT_TYPE_STYLE[typeKey].sx} />
                                </Box>
                            );
                        })}
                        <Button size="small" startIcon={<Edit sx={{ fontSize: 14 }} />} onClick={() => onRowClick(cat.parentSKU)} sx={{ alignSelf: 'flex-start' }}>
                            Edit Bundle
                        </Button>
                    </Stack>
                </Collapse>
            </CardContent>
        </Card>
    );
};

// ── GB / Bundle View Table ────────────────────────────────────────────────────

type BundleSortKey = 'bundleSku' | 'physicalDoc' | 'potentialDoc' | 'drr' | 'inventory' | 'canBeMade' | 'cogs';

type ProcurementQty = { poNotPlaced: number; ordered: number; inTransit: number; grnPending: number } | null | undefined;

// Procurement pipeline stages in the order stock moves toward us (closest to arriving first),
// each with its own tone so a column reads at a glance without leaning on the header text.
const PROCUREMENT_COLUMNS: { key: 'grnPending' | 'inTransit' | 'ordered' | 'poNotPlaced'; label: string; hint: string; color: string; tint: string }[] = [
    { key: 'grnPending', label: 'Delivered (GRN Pending)', hint: 'Delivered, GRN not done yet', color: '#15803D', tint: 'rgba(22, 163, 74, 0.14)' },
    { key: 'inTransit', label: 'In Transit', hint: 'Dispatched, on the way', color: '#B8451E', tint: 'rgba(220, 88, 41, 0.14)' },
    { key: 'ordered', label: 'Ordered', hint: 'PO placed, not dispatched', color: '#4A433D', tint: 'rgba(107, 98, 91, 0.14)' },
    { key: 'poNotPlaced', label: 'Reorder Not Placed', hint: 'Needed, no PO placed yet', color: '#b45309', tint: 'rgba(245, 158, 11, 0.18)' },
];

const PIPELINE_DIVIDER = '1px solid #ECECE4';

// No procurement record for the SKU reads as a dash; a real zero is a faint 0; a quantity is a
// tinted pill in the stage's colour.
const ProcurementCells: React.FC<{ qty?: ProcurementQty }> = ({ qty }) => (
    <>
        {PROCUREMENT_COLUMNS.map((c, i) => {
            const v = qty ? qty[c.key] : undefined;
            return (
                <TableCell key={c.key} align="center" sx={{ ...(i === 0 ? { borderLeft: PIPELINE_DIVIDER } : {}), ...(i === 3 ? { borderRight: PIPELINE_DIVIDER } : {}) }}>
                    {v === undefined ? (
                        <Typography component="span" sx={{ color: 'text.disabled' }}>—</Typography>
                    ) : v === 0 ? (
                        <Typography component="span" aria-label="0" sx={{ color: '#cfcfc6', fontSize: '1rem', lineHeight: 1 }}>·</Typography>
                    ) : (
                        <Box component="span" sx={{ display: 'inline-block', minWidth: 44, px: 1, py: 0.2, borderRadius: '999px', bgcolor: c.tint, color: c.color, fontFamily: 'monospace', fontWeight: 800, fontSize: '0.78rem', fontVariantNumeric: 'tabular-nums' }}>
                            {v.toLocaleString('en-IN')}
                        </Box>
                    )}
                </TableCell>
            );
        })}
    </>
);

const MotionTableRow = motion(TableRow);

// ── Detailed view table ──────────────────────────────────────────────────────
// A denser layout for the Detailed view: one pinned "Bundle" column (thumbnail, SKU ID and
// name — Parent SKU, Image, SKU Name and Virtual Inventory are folded in or dropped because
// they repeated information or were always empty), Status and Type share a cell, Physical and
// Potential DOC share a cell. Sorting works exactly as in the normal table; every sortable
// header keeps its own sort control. Child rows carry stock + the four pipeline stages.

const DETAIL_BORDER = TABLE_BORDER;
// Detailed view pins Parent SKU + SKU ID so a child row never loses its name while scrolling.
const D_PARENT_W = 190;
const D_SKU_W = 250;
const HEAD_SX = {
    bgcolor: 'rgba(250, 250, 247, 0.92)',
    backdropFilter: 'blur(8px)',
    color: '#6B625B',
    fontWeight: 700,
    fontSize: '0.66rem',
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    lineHeight: 1.2,
    py: 1.25,
    borderBottom: `1px solid ${DETAIL_BORDER}`,
};

type ChildComp = { sku: string; componentType?: 'PACKAGING' | 'ADD_ON' | 'SKU'; inventory?: number; isLimiting?: boolean; procurement?: ProcurementQty; cogs?: number | null; cogsPct?: number | null; extra?: boolean };

const ChildRowDetailed: React.FC<{ comp: ChildComp; index: number; isLast: boolean }> = ({ comp, index, isLast }) => {
    const reduceMotion = useReducedMotion();
    const typeKey = comp.componentType || 'SKU';
    const out = comp.inventory !== undefined && comp.inventory <= 0;
    const bg = comp.isLimiting ? '#fff6ef' : '#fcfcf9';
    return (
        <MotionTableRow
            initial={reduceMotion ? false : { opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -6 }}
            transition={{ duration: 0.22, delay: Math.min(index, 8) * 0.03, ease: [0.16, 1, 0.3, 1] }}
            sx={{ bgcolor: bg, '& .MuiTableCell-body': { py: 0.55, borderBottom: isLast ? 'none' : `1px solid #f3f3ec` } }}
        >
            <TableCell sx={{ position: 'sticky', left: 0, zIndex: 2, bgcolor: bg, width: D_PARENT_W, minWidth: D_PARENT_W, maxWidth: D_PARENT_W, ...(comp.isLimiting ? { boxShadow: 'inset 3px 0 0 #DC5829' } : {}) }} />
            <TableCell sx={{ position: 'sticky', left: D_PARENT_W, zIndex: 2, bgcolor: bg, width: D_SKU_W, minWidth: D_SKU_W, maxWidth: D_SKU_W, pl: 3 }}>
                <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <Box sx={{ position: 'absolute', left: -14, top: -14, bottom: isLast ? 'calc(50% - 0px)' : -14, width: 2, bgcolor: '#e6dccf', borderRadius: 1 }} />
                    <Box sx={{ position: 'absolute', left: -14, top: '50%', width: 12, height: 2, bgcolor: '#e6dccf', borderRadius: 1 }} />
                    <Tooltip title={comp.sku}>
                        <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{comp.sku}</Box>
                    </Tooltip>
                </Box>
            </TableCell>
            <TableCell colSpan={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Chip label={typeKey === 'ADD_ON' ? 'Add-On' : typeKey === 'PACKAGING' ? 'Packaging' : 'SKU'} size="small" variant="outlined" sx={{ fontSize: '0.62rem', height: 18, ...COMPONENT_TYPE_STYLE[typeKey].sx }} />
                    {comp.isLimiting && (
                        <Tooltip title="Lowest stock — this child limits Can Be Made">
                            <Chip label="Limits Can Be Made" size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 800, bgcolor: 'rgba(220, 88, 41, 0.16)', color: '#B8451E' }} />
                        </Tooltip>
                    )}
                </Box>
            </TableCell>
            <TableCell sx={{ fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums', fontWeight: 800, fontSize: '0.84rem', color: out ? '#b91c1c' : 'text.primary' }}>
                {comp.inventory === undefined ? '—' : comp.inventory.toLocaleString('en-IN')}
            </TableCell>
            <ProcurementCells qty={comp.procurement} />
            <TableCell />
            <TableCell>
                {comp.cogs === undefined ? (
                    <Typography component="span" sx={{ color: 'text.disabled' }}>—</Typography>
                ) : comp.cogs === null ? (
                    <Tooltip title="No COGS found for this SKU in Redshift">
                        <Typography component="span" sx={{ color: '#b45309', fontWeight: 700, fontSize: '0.74rem', cursor: 'help' }}>Missing</Typography>
                    </Tooltip>
                ) : (
                    <Tooltip title={comp.extra ? 'Packaging attribute of the bundle — not part of the bundle COGS' : ''}>
                        <Typography component="span" sx={{ fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums', fontWeight: 700, fontSize: '0.82rem', color: comp.extra ? 'text.disabled' : 'text.primary' }}>{formatInr(comp.cogs)}</Typography>
                    </Tooltip>
                )}
            </TableCell>
            <TableCell>
                {comp.cogsPct === null || comp.cogsPct === undefined ? (
                    <Typography component="span" sx={{ color: 'text.disabled' }}>—</Typography>
                ) : (
                    // Same mini share-bar (shared CogsShareBar) + brand tint ramp as the detail
                    // view's cost-composition legend, so a child reads identically in both places.
                    <CogsShareBar pct={comp.cogsPct} color={cogsSegmentColor(index)} delay={Math.min(index, 8) * 0.03} />
                )}
            </TableCell>
            <TableCell />
        </MotionTableRow>
    );
};

const BundleDetailedTable: React.FC<{
    rows: BundleCategoryRow[];
    sortKey: BundleSortKey;
    sortDir: 'asc' | 'desc';
    onSortChange: (key: BundleSortKey) => void;
    onRowClick: (parentSKU: string) => void;
    onImageClick: (title: string, images: string[]) => void;
    page: number; total: number; rowsPerPage: number; onPageChange: (page: number) => void;
}> = ({ rows, sortKey, sortDir, onSortChange, onRowClick, onImageClick, page, total, rowsPerPage, onPageChange }) => {
    const reduceMotion = useReducedMotion();
    // Every parent's children are open by default; this holds the ones the user folded.
    const [folded, setFolded] = useState<Set<string>>(new Set());
    const allFolded = rows.length > 0 && rows.every(r => folded.has(r.parentSKU));
    const sortLabel = (key: BundleSortKey, label: string) => (
        <TableSortLabel active={sortKey === key} direction={sortDir} onClick={() => onSortChange(key)} sx={{ '& .MuiTableSortLabel-icon': { opacity: 1 }, color: 'inherit !important', '&.Mui-active': { color: '#1A140F !important' } }}>
            {label}
        </TableSortLabel>
    );

    return (
        <Box>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '14px', borderColor: DETAIL_BORDER, boxShadow: '0 1px 2px rgba(26,20,15,0.04), 0 10px 28px rgba(26,20,15,0.05)', overflow: 'auto' }}>
                <Table size="small" stickyHeader sx={{ width: '100%', minWidth: 1700 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ ...HEAD_SX, position: 'sticky', left: 0, zIndex: 4, width: D_PARENT_W, minWidth: D_PARENT_W, maxWidth: D_PARENT_W }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Tooltip title={allFolded ? 'Expand all' : 'Collapse all'}>
                                        <IconButton
                                            size="small"
                                            aria-label={allFolded ? 'Expand all bundles' : 'Collapse all bundles'}
                                            onClick={() => setFolded(allFolded ? new Set() : new Set(rows.map(r => r.parentSKU)))}
                                            sx={{ p: 0.25, color: '#6B625B' }}
                                        >
                                            <motion.span animate={{ rotate: allFolded ? 0 : 90 }} transition={{ duration: 0.18 }} style={{ display: 'inline-flex' }}>
                                                <ChevronRight sx={{ fontSize: 18 }} />
                                            </motion.span>
                                        </IconButton>
                                    </Tooltip>
                                    Parent SKU
                                </Box>
                            </TableCell>
                            <TableCell sx={{ ...HEAD_SX, position: 'sticky', left: D_PARENT_W, zIndex: 4, width: D_SKU_W, minWidth: D_SKU_W, maxWidth: D_SKU_W }}>{sortLabel('bundleSku', 'SKU ID')}</TableCell>
                            <TableCell sx={HEAD_SX}>Status</TableCell>
                            <TableCell sx={HEAD_SX}>SKU Name</TableCell>
                            <TableCell sx={HEAD_SX}>Type</TableCell>
                            <TableCell sx={HEAD_SX}>{sortLabel('physicalDoc', 'Physical DOC')}</TableCell>
                            <TableCell sx={HEAD_SX}>{sortLabel('potentialDoc', 'Potential DOC')}</TableCell>
                            <TableCell sx={HEAD_SX}>{sortLabel('drr', 'DRR (7-day)')}</TableCell>
                            <TableCell sx={HEAD_SX}>{sortLabel('inventory', 'Inventory')}</TableCell>
                            {PROCUREMENT_COLUMNS.map((c, i) => (
                                <TableCell key={c.key} align="center" sx={{ ...HEAD_SX, borderTop: `3px solid ${c.color}`, color: `${c.color} !important`, whiteSpace: 'nowrap', ...(i === 0 ? { borderLeft: PIPELINE_DIVIDER } : {}), ...(i === 3 ? { borderRight: PIPELINE_DIVIDER } : {}) }}>
                                    <Tooltip title={c.hint}><span>{c.label}</span></Tooltip>
                                </TableCell>
                            ))}
                            <TableCell sx={HEAD_SX}>{sortLabel('canBeMade', 'Can Be Made')}</TableCell>
                            <TableCell sx={{ ...HEAD_SX, whiteSpace: 'nowrap' }}>
                                <Tooltip title="Sum of the children's COGS, post-GST (Redshift current_inventory_cogs)"><span>{sortLabel('cogs', 'COGS (post-GST)')}</span></Tooltip>
                            </TableCell>
                            <TableCell sx={{ ...HEAD_SX, whiteSpace: 'nowrap' }}>
                                <Tooltip title="Each child's share of the bundle's COGS"><span>% of total</span></Tooltip>
                            </TableCell>
                            <TableCell sx={{ ...HEAD_SX, width: 44 }} />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((cat) => {
                            const b = cat.activeBundle!;
                            const type = (b as any).saleType === 'Simple' ? 'Simple' : 'Bundle';
                            const active = (b as any).isActive !== false;
                            const physicalDoc = (b as any).physicalDoc !== undefined ? (b as any).physicalDoc as number | null : (b.drr && b.drr > 0 && b.inventory ? b.inventory / b.drr : null);
                            const potentialDoc = (b as any).potentialDoc !== undefined ? (b as any).potentialDoc as number | null : (b.drr && b.drr > 0 && b.canBeMade ? b.canBeMade / b.drr : null);
                            const comps: ChildComp[] = ((b as any).componentsList !== undefined
                                ? (b as any).componentsList
                                : (b.childs || []).map((sku: string) => ({ sku }))) as ChildComp[];
                            const kids = [...comps].sort((x, y) => (x.inventory ?? Infinity) - (y.inventory ?? Infinity));
                            const open = !folded.has(cat.parentSKU);
                            const limiting = kids.filter(k => k.isLimiting);
                            const images = b.images && b.images.length > 0 ? b.images : (b.imageURL && b.imageURL.startsWith('http') ? [b.imageURL] : []);

                            return (
                                <React.Fragment key={cat.parentSKU}>
                                    <TableRow
                                        hover
                                        onClick={() => onRowClick(cat.parentSKU)}
                                        sx={{ cursor: 'pointer', bgcolor: 'white', '&:hover': { bgcolor: '#fffaf6' }, '& .MuiTableCell-body': { py: 1, borderTop: `1px solid ${DETAIL_BORDER}`, borderBottom: 'none' } }}
                                    >
                                        <TableCell sx={{ position: 'sticky', left: 0, zIndex: 2, bgcolor: 'inherit', width: D_PARENT_W, minWidth: D_PARENT_W, maxWidth: D_PARENT_W }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                                                <IconButton
                                                    size="small"
                                                    aria-label={open ? `Hide children of ${b.bundleSku}` : `Show children of ${b.bundleSku}`}
                                                    aria-expanded={open}
                                                    onClick={(e) => { e.stopPropagation(); setFolded(prev => { const n = new Set(prev); if (n.has(cat.parentSKU)) n.delete(cat.parentSKU); else n.add(cat.parentSKU); return n; }); }}
                                                    sx={{ p: 0.25, flexShrink: 0 }}
                                                >
                                                    <motion.span animate={{ rotate: open ? 90 : 0 }} transition={{ duration: 0.18 }} style={{ display: 'inline-flex' }}>
                                                        <ChevronRight sx={{ fontSize: 18 }} />
                                                    </motion.span>
                                                </IconButton>
                                                <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.8rem', lineHeight: 1.25, wordBreak: 'break-word' }}>{cat.parentSKU}</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ position: 'sticky', left: D_PARENT_W, zIndex: 2, bgcolor: 'inherit', width: D_SKU_W, minWidth: D_SKU_W, maxWidth: D_SKU_W, fontFamily: 'monospace', fontWeight: 800, fontSize: '0.84rem', wordBreak: 'break-word' }}>
                                            {b.bundleSku}
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={active ? 'Active' : 'Inactive'} size="small" sx={{ height: 20, fontSize: '0.66rem', fontWeight: 700, bgcolor: active ? 'rgba(34,197,94,0.14)' : 'rgba(100,116,139,0.14)', color: active ? '#15803d' : '#4A433D' }} />
                                        </TableCell>
                                        <TableCell sx={{ minWidth: 230, maxWidth: 300 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                                <Box sx={{ flexShrink: 0 }}>
                                                    <ImageThumb images={images} label={b.bundleSku} size={36} onClick={(e) => { e?.stopPropagation(); if (images.length > 0) onImageClick(b.bundleSku, images); }} />
                                                </Box>
                                                <Typography sx={{ fontSize: '0.76rem', lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{b.name || '—'}</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={type} size="small" variant="outlined" color={type === 'Simple' ? 'primary' : 'default'} sx={{ height: 20, fontSize: '0.66rem', fontWeight: 600 }} />
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={formatDecimal(physicalDoc)} size="small" color={docColorBundle(physicalDoc)} variant={physicalDoc !== null ? 'filled' : 'outlined'} sx={{ fontWeight: 700, height: 22, minWidth: 46, fontSize: '0.72rem' }} />
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={formatDecimal(potentialDoc)} size="small" color={docColorBundle(potentialDoc)} variant={potentialDoc !== null ? 'filled' : 'outlined'} sx={{ fontWeight: 700, height: 22, minWidth: 46, fontSize: '0.72rem' }} />
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums' }}>
                                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
                                                {formatDecimal(b.drr)}
                                                {(b as any).drrSource === 'inherited' && (
                                                    <Tooltip title={`Carried over from v${(b as any).inheritedFromVersion} — this version's own sales data hasn't caught up yet`}>
                                                        <History sx={{ fontSize: 13, color: '#f59e0b', cursor: 'help' }} />
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{formatNumber(b.inventory)}</TableCell>
                                        <TableCell colSpan={4} sx={{ borderLeft: PIPELINE_DIVIDER, borderRight: PIPELINE_DIVIDER }}>
                                            <motion.div initial={reduceMotion ? false : { opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.25 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, flexWrap: 'wrap', color: 'text.secondary', fontSize: '0.74rem' }}>
                                                    <span>{kids.length} {kids.length === 1 ? 'child' : 'children'}</span>
                                                    {limiting.length > 0 && (
                                                        <Tooltip title="The child with the lowest stock sets Can Be Made">
                                                            <Chip
                                                                size="small"
                                                                label={`Limited by ${limiting[0].sku} · ${(limiting[0].inventory ?? 0).toLocaleString('en-IN')}${limiting.length > 1 ? ` +${limiting.length - 1}` : ''}`}
                                                                sx={{ height: 20, fontSize: '0.64rem', fontWeight: 700, bgcolor: 'rgba(220, 88, 41, 0.12)', color: '#B8451E', maxWidth: 360 }}
                                                            />
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </motion.div>
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{formatNumber(b.canBeMade)}</TableCell>
                                        <TableCell><CogsTotalCell cogs={(b as any).cogs as BundleCogs} /></TableCell>
                                        <TableCell>
                                            {((b as any).cogs as BundleCogs)?.total != null && (
                                                <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: '0.74rem', color: 'text.secondary' }}>100%</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ width: 44, px: 1 }}>
                                            <Tooltip title="Edit bundle">
                                                <IconButton size="small" sx={{ color: '#6B625B', '&:hover': { color: '#DC5829' } }} onClick={(e) => { e.stopPropagation(); onRowClick(cat.parentSKU); }}>
                                                    <Edit sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                    <AnimatePresence initial={false}>
                                        {open && kids.map((comp, i) => (
                                            <ChildRowDetailed key={`${cat.parentSKU}-${comp.sku}`} comp={comp} index={i} isLast={i === kids.length - 1} />
                                        ))}
                                    </AnimatePresence>
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <PaginationBar page={page} total={total} rowsPerPage={rowsPerPage} onPageChange={onPageChange} />
        </Box>
    );
};

const BundleTableView: React.FC<{
    rows: BundleCategoryRow[];
    loading: boolean;
    error: string | null;
    onRetry: () => void;
    onRowClick: (parentSKU: string) => void;
    page: number;
    total: number;
    rowsPerPage: number;
    onPageChange: (page: number) => void;
    sortBy: string;
    onSortChange: (key: BundleSortKey) => void;
    onImageClick: (title: string, images: string[]) => void;
    detailed: boolean;
}> = ({ rows, loading, error, onRetry, onRowClick, page, total, rowsPerPage, onPageChange, sortBy, onSortChange, onImageClick, detailed }) => {
    const [expandedRow] = useState<string | null>(null);
    // Separate from expandedRow: each of the 3 split component columns (SKU/Add-On/
    // Packaging) expands independently, keyed by `${parentSKU}-${typeKey}`.
    const [expandedComponentCell, setExpandedComponentCell] = useState<string | null>(null);
    // Sorting is applied server-side (across the FULL matching dataset, before
    // pagination) so a column-header click re-fetches rather than re-ordering only the
    // currently-loaded page — otherwise sorting looked broken as soon as you paginated.
    const [sortKeyRaw, sortDirRaw] = sortBy.split('-');
    const sortKey = (sortKeyRaw || 'bundleSku') as BundleSortKey;
    const sortDir: 'asc' | 'desc' = sortDirRaw === 'desc' ? 'desc' : 'asc';

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const handleSort = (key: BundleSortKey) => onSortChange(key);

    const filteredRows = useMemo(() => rows.filter(r => r.activeBundle), [rows]);

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
    if (error) return <Alert severity="error" action={<Button onClick={onRetry}>Retry</Button>}>{error}</Alert>;
    if (total === 0) return <Alert severity="info">No active bundles found.</Alert>;

    if (isMobile) {
        return (
            <Box>
                {filteredRows.map(cat => (
                    <BundleMobileCard key={cat.parentSKU} cat={cat} onRowClick={onRowClick} onImageClick={onImageClick} />
                ))}
                <PaginationBar page={page} total={total} rowsPerPage={rowsPerPage} onPageChange={onPageChange} />
            </Box>
        );
    }

    if (detailed) {
        return (
            <BundleDetailedTable
                rows={filteredRows}
                sortKey={sortKey}
                sortDir={sortDir}
                onSortChange={onSortChange}
                onRowClick={onRowClick}
                onImageClick={onImageClick}
                page={page}
                total={total}
                rowsPerPage={rowsPerPage}
                onPageChange={onPageChange}
            />
        );
    }

    return (
        <Box>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '14px', borderColor: DETAIL_BORDER, boxShadow: '0 1px 2px rgba(26,20,15,0.04), 0 10px 28px rgba(26,20,15,0.05)' }}>
                <Table size="small" stickyHeader sx={{ minWidth: 1340, '& .MuiTableRow-root:not(:last-child) .MuiTableCell-body': { borderBottom: `1px solid ${DETAIL_BORDER}` }, '& .MuiTableRow-root:last-child .MuiTableCell-body': { borderBottom: 'none' } }}>
                    <TableHead>
                        <TableRow sx={{ '& .MuiTableCell-head': { bgcolor: 'rgba(250, 250, 247, 0.92)', backdropFilter: 'blur(8px)', fontWeight: 700, fontSize: '0.66rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6B625B', lineHeight: 1.2, py: 1.25 } }}>
                            <TableCell sx={{ fontWeight: 700, position: 'sticky', left: 0, bgcolor: '#fafaf7', zIndex: 3 }}>
                                Parent SKU
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>
                                <TableSortLabel active={sortKey === 'bundleSku'} direction={sortDir} onClick={() => handleSort('bundleSku')} sx={{ '& .MuiTableSortLabel-icon': { opacity: 1 } }}>
                                    SKU ID
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Image</TableCell>
                            <TableCell>SKU Name</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>
                                <TableSortLabel active={sortKey === 'physicalDoc'} direction={sortDir} onClick={() => handleSort('physicalDoc')} sx={{ '& .MuiTableSortLabel-icon': { opacity: 1 } }}>
                                    Physical DOC
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                <TableSortLabel active={sortKey === 'potentialDoc'} direction={sortDir} onClick={() => handleSort('potentialDoc')} sx={{ '& .MuiTableSortLabel-icon': { opacity: 1 } }}>
                                    Potential DOC
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                <TableSortLabel active={sortKey === 'drr'} direction={sortDir} onClick={() => handleSort('drr')} sx={{ '& .MuiTableSortLabel-icon': { opacity: 1 } }}>
                                    DRR (7-day)
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                <TableSortLabel active={sortKey === 'inventory'} direction={sortDir} onClick={() => handleSort('inventory')} sx={{ '& .MuiTableSortLabel-icon': { opacity: 1 } }}>
                                    Physical Inventory
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                <TableSortLabel active={sortKey === 'canBeMade'} direction={sortDir} onClick={() => handleSort('canBeMade')} sx={{ '& .MuiTableSortLabel-icon': { opacity: 1 } }}>
                                    Can Be Made
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                <Tooltip title="Sum of the children's COGS, post-GST. Expand a bundle in Detailed view for each child's cost and share.">
                                    <TableSortLabel active={sortKey === 'cogs'} direction={sortDir} onClick={() => handleSort('cogs')} sx={{ '& .MuiTableSortLabel-icon': { opacity: 1 } }}>
                                        COGS (post-GST)
                                    </TableSortLabel>
                                </Tooltip>
                            </TableCell>
                            <TableCell>SKU Components</TableCell>
                            <TableCell>Add-Ons</TableCell>
                            <TableCell>Packaging Components</TableCell>
                            <TableCell sx={{ width: 44, px: 1 }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredRows.map((cat) => {
                            const b = cat.activeBundle!;

                            // Type: uses the authoritative saleType dropdown ("Simple"/"Bundle"), not
                            // the free-text category field — category can carry stale "gift"-containing
                            // text for bundles whose saleType is actually "Bundle", which previously
                            // misclassified Combo Bundles as Simple here.
                            const type = (b as any).saleType === 'Simple' ? 'Simple' : 'Bundle';

                            // Physical/Potential DOC
                            const physicalDoc = (b as any).physicalDoc !== undefined
                                ? (b as any).physicalDoc as number | null
                                : (b.drr && b.drr > 0 && b.inventory ? b.inventory / b.drr : null);
                            const potentialDoc = (b as any).potentialDoc !== undefined
                                ? (b as any).potentialDoc as number | null
                                : (b.drr && b.drr > 0 && b.canBeMade ? b.canBeMade / b.drr : null);

                            // Components: prefer componentsList, fall back to childs
                            const componentsList: Array<{ sku: string; isPackaging: boolean; componentType?: 'PACKAGING' | 'ADD_ON' | 'SKU'; inventory?: number; isLimiting?: boolean }> = (b as any).componentsList !== undefined
                                ? (b as any).componentsList as Array<{ sku: string; isPackaging: boolean; componentType?: 'PACKAGING' | 'ADD_ON' | 'SKU'; inventory?: number; isLimiting?: boolean }>
                                : (b.childs || []).map((sku: string) => ({ sku, isPackaging: false, componentType: 'SKU' as const }));

                            const isExpanded = expandedRow === cat.parentSKU;

                            return (
                                <TableRow
                                    key={cat.parentSKU}
                                    hover
                                    sx={{
                                        cursor: 'pointer',
                                        bgcolor: isExpanded ? 'rgba(220, 88, 41, 0.06)' : 'inherit',
                                        '&:hover': { bgcolor: isExpanded ? 'rgba(220, 88, 41, 0.1)' : 'action.hover' },
                                    }}
                                    onClick={() => onRowClick(cat.parentSKU)}
                                >
                                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600, position: 'sticky', left: 0, bgcolor: 'white', zIndex: 2 }}>
                                        {cat.parentSKU}
                                    </TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                        {b.bundleSku}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={(b as any).isActive === false ? 'Inactive' : 'Active'}
                                            size="small"
                                            color={(b as any).isActive === false ? 'default' : 'success'}
                                            variant={(b as any).isActive === false ? 'outlined' : 'filled'}
                                            sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <ImageThumb
                                            images={b.images && b.images.length > 0 ? b.images : (b.imageURL && b.imageURL.startsWith('http') ? [b.imageURL] : [])}
                                            label={b.bundleSku}
                                            onClick={(e) => {
                                                e?.stopPropagation();
                                                const allImages = b.images && b.images.length > 0 ? b.images : (b.imageURL && b.imageURL.startsWith('http') ? [b.imageURL] : []);
                                                if (allImages.length > 0) {
                                                    onImageClick(b.bundleSku, allImages);
                                                }
                                            }}
                                            size={36}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.7rem' }}>{b.name || '—'}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={type}
                                            size="small"
                                            color={type === 'Simple' ? 'primary' : 'default'}
                                            variant="outlined"
                                            sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={formatDecimal(physicalDoc)}
                                            size="small"
                                            color={docColorBundle(physicalDoc)}
                                            variant={physicalDoc !== null ? 'filled' : 'outlined'}
                                            sx={{ fontWeight: 600, minWidth: 48 }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={formatDecimal(potentialDoc)}
                                            size="small"
                                            color={docColorBundle(potentialDoc)}
                                            variant={potentialDoc !== null ? 'filled' : 'outlined'}
                                            sx={{ fontWeight: 600, minWidth: 48 }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace' }}>
                                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
                                            {formatDecimal(b.drr)}
                                            {(b as any).drrSource === 'inherited' && (
                                                <Tooltip title={`Carried over from v${(b as any).inheritedFromVersion} — this version's own sales data hasn't caught up yet`}>
                                                    <History sx={{ fontSize: 13, color: '#f59e0b', cursor: 'help' }} />
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace' }}>{formatNumber(b.inventory)}</TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace' }}>{formatNumber(b.canBeMade)}</TableCell>
                                    <TableCell><CogsTotalCell cogs={(b as any).cogs as BundleCogs} /></TableCell>
                                    {(['SKU', 'ADD_ON', 'PACKAGING'] as const).map((typeKey) => {
                                        const itemsOfType = componentsList.filter(c => (c.componentType || 'SKU') === typeKey);
                                        const rowKey = `${cat.parentSKU}-${typeKey}`;
                                        return (
                                            <TableCell key={rowKey}>
                                                {itemsOfType.length > 0 ? (
                                                    <ComponentStockChips
                                                        items={itemsOfType}
                                                        max={3}
                                                        expanded={expandedComponentCell === rowKey}
                                                        onToggle={() => setExpandedComponentCell(expandedComponentCell === rowKey ? null : rowKey)}
                                                        chipSx={COMPONENT_TYPE_STYLE[typeKey].sx}
                                                    />
                                                ) : (
                                                    <Typography variant="caption" color="text.disabled">—</Typography>
                                                )}
                                            </TableCell>
                                        );
                                    })}
                                    <TableCell sx={{ width: 44, px: 1 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }} onClick={e => e.stopPropagation()}>
                                            <Tooltip title="Edit bundle">
                                                <IconButton
                                                    size="small"
                                                    sx={{ color: 'text.primary', '&:hover': { color: '#DC5829' } }}
                                                    onClick={(e) => { e.stopPropagation(); onRowClick(cat.parentSKU); }}
                                                >
                                                    <Edit sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <PaginationBar page={page} total={total} rowsPerPage={rowsPerPage} onPageChange={onPageChange} />
        </Box>
    );
};

// ── Component View Table ─────────────────────────────────────────────────────

type CompSortKey = 'reorderQty' | 'doc' | 'skuInventory' | 'gbInventory' | 'bundleInventory' | 'inactiveInStockCount' | 'totalDrr' | 'skuId' | 'individualDrr' | 'gbDrr' | 'bundleDrr' | 'gbCount' | 'bundleCount';

// Maps the frontend's column keys to the backend's sortFns field names (they differ for
// skuInventory/totalDrr, which the backend calls inventory/drr) — see getComponentView's
// sortFns in backend/models/BundleCategory.js.
const COMP_SORT_FIELD_MAP: Record<CompSortKey, string> = {
    reorderQty: 'reorderQty',
    doc: 'doc',
    skuInventory: 'inventory',
    gbInventory: 'gbInventory',
    bundleInventory: 'bundleInventory',
    inactiveInStockCount: 'inactiveInStockCount',
    totalDrr: 'drr',
    skuId: 'skuId',
    individualDrr: 'individualDrr',
    gbDrr: 'gbDrr',
    bundleDrr: 'bundleDrr',
    gbCount: 'gbCount',
    bundleCount: 'bundleCount',
};

// skuId/skuName use asc/desc suffixes on the backend; every numeric column uses high/low.
const buildCompSortBy = (key: CompSortKey, dir: 'asc' | 'desc'): string => {
    const field = COMP_SORT_FIELD_MAP[key];
    const suffix = key === 'skuId' ? dir : (dir === 'asc' ? 'low' : 'high');
    return `${field}-${suffix}`;
};

const parseCompSortBy = (sortByStr: string): { key: CompSortKey; dir: 'asc' | 'desc' } => {
    const m = sortByStr.match(/^(.+)-(asc|desc|high|low)$/);
    if (!m) return { key: 'reorderQty', dir: 'desc' };
    const [, field, suffix] = m;
    const key = (Object.keys(COMP_SORT_FIELD_MAP) as CompSortKey[]).find(k => COMP_SORT_FIELD_MAP[k] === field) || 'reorderQty';
    const dir: 'asc' | 'desc' = (suffix === 'asc' || suffix === 'low') ? 'asc' : 'desc';
    return { key, dir };
};

// ── Mobile card view — Component View ────────────────────────────────────────
// Leads with Reorder Qty rather than identity-first — this is the number someone's
// actually scanning for on a component list ("does this need attention"), unlike the
// Simple/Bundle view where identity leads. Product Category next (the other thing worth
// seeing without tapping), DOC/DRR/inventory breakdowns and Simple/Bundle usage behind
// tap-to-expand.
const ComponentMobileCard: React.FC<{
    row: ComponentViewRow;
    categoryColorMap: Map<string, object>;
    onImageClick: (title: string, images: string[]) => void;
}> = ({ row, categoryColorMap, onImageClick }) => {
    const [expanded, setExpanded] = useState(false);
    const images = row.images && row.images.length > 0 ? row.images : (row.skuImage && row.skuImage.startsWith('http') ? [row.skuImage] : []);

    return (
        <Card
            variant="outlined"
            sx={{
                mb: 1.5,
                borderLeft: row.productCategory ? undefined : '3px solid #dc2626',
                bgcolor: row.productCategory ? undefined : 'rgba(220, 38, 38, 0.04)',
            }}
        >
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                    <ImageThumb
                        images={images}
                        label={row.skuId}
                        onClick={(e) => { e?.stopPropagation(); if (images.length > 0) onImageClick(row.skuId, images); }}
                        size={44}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
                            <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.85rem' }}>{row.skuId}</Typography>
                            <Chip
                                size="small"
                                label={`Reorder: ${formatNumber(row.reorderQty)}`}
                                sx={{
                                    fontWeight: 700,
                                    fontSize: '0.65rem',
                                    height: 20,
                                    bgcolor: row.reorderQty > 0 ? 'rgba(239, 68, 68, 0.15)' : 'rgba(148, 163, 184, 0.15)',
                                    color: row.reorderQty > 0 ? 'error.main' : 'text.disabled',
                                }}
                            />
                        </Box>
                        {row.skuName !== row.skuId && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{row.skuName}</Typography>
                        )}
                        <Box sx={{ mt: 0.5 }}>
                            <Chip
                                label={row.productCategory || '⚠️ Blank — needs fix'}
                                size="small"
                                variant={row.productCategory ? 'outlined' : 'filled'}
                                sx={{
                                    fontSize: '0.65rem',
                                    height: 20,
                                    ...(row.productCategory
                                        ? (categoryColorMap.get(row.productCategory) || COMPONENT_TYPE_STYLE.SKU.sx)
                                        : COMPONENT_TYPE_STYLE.BLANK.sx),
                                }}
                            />
                        </Box>
                    </Box>
                    <IconButton
                        size="small"
                        onClick={() => setExpanded(x => !x)}
                        sx={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
                    >
                        <ExpandMore fontSize="small" />
                    </IconButton>
                </Box>

                <Collapse in={expanded}>
                    <Divider sx={{ my: 1.25 }} />
                    <Stack spacing={1}>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Chip size="small" label={`DOC: ${formatDecimal(row.doc)}`} color={docColorComponent(row.doc)} variant={row.doc !== null ? 'filled' : 'outlined'} sx={{ fontWeight: 600 }} />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Typography variant="caption">SKU Inventory: <strong>{formatNumber(row.skuInventory)}</strong></Typography>
                            <Typography variant="caption">Simple Inventory (Active): <strong>{formatNumber(row.gbInventory)}</strong></Typography>
                            <Typography variant="caption" sx={{ color: row.inactiveInStockCount > 0 ? 'warning.dark' : undefined }}>Simple Inventory (Inactive): <strong>{formatNumber(row.inactiveInStockCount)} SKUs</strong></Typography>
                            <Typography variant="caption">Total DRR: <strong>{formatDecimal(row.totalDrr)}</strong></Typography>
                            <Typography variant="caption">Individual DRR: <strong>{formatDecimal(row.individualDrr)}</strong></Typography>
                            <Typography variant="caption">Simple DRR: <strong>{formatDecimal(row.gbDrr)}</strong></Typography>
                            <Typography variant="caption">Bundle DRR: <strong>{formatDecimal(row.bundleDrr)}</strong></Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.3 }}>Simple ({row.gbCount})</Typography>
                            {row.gbList.length > 0 ? (
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                    {row.gbList.map(s => <Chip key={s} label={s} size="small" variant="outlined" />)}
                                </Box>
                            ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.3 }}>Bundle ({row.bundleCount})</Typography>
                            {row.bundleList.length > 0 ? (
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                    {row.bundleList.map(s => <Chip key={s} label={s} size="small" variant="outlined" />)}
                                </Box>
                            ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                        </Box>
                    </Stack>
                </Collapse>
            </CardContent>
        </Card>
    );
};

const ComponentViewTable: React.FC<{
    rows: ComponentViewRow[];
    loading: boolean;
    error: string | null;
    onRetry: () => void;
    targetDoc: number;
    page: number;
    total: number;
    rowsPerPage: number;
    onPageChange: (page: number) => void;
    sortBy: string;
    onSortChange: (key: CompSortKey) => void;
    onImageClick: (title: string, images: string[]) => void;
    categoryOptions: string[];
}> = ({ rows, loading, error, onRetry, targetDoc, page, total, rowsPerPage, onPageChange, sortBy, onSortChange, onImageClick, categoryOptions }) => {
    const categoryColorMap = useMemo(() => buildCategoryColorMap(categoryOptions), [categoryOptions]);
    // Sorting is applied server-side across the FULL matching dataset before pagination —
    // a column-header click re-fetches with the new sortBy rather than re-ordering only
    // the currently-loaded page (which broke as soon as you paginated).
    const { key: sortKey, dir: sortDir } = parseCompSortBy(sortBy);
    const [expandedRow, setExpandedRow] = useState<string | null>(null);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const handleSort = (key: CompSortKey) => onSortChange(key);

    const sorted = rows;

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
    if (error) return <Alert severity="error" action={<Button onClick={onRetry}>Retry</Button>}>{error}</Alert>;
    if (total === 0) return <Alert severity="info">No components match the current filters.</Alert>;

    if (isMobile) {
        return (
            <Box>
                {sorted.map(row => (
                    <ComponentMobileCard key={row.skuId} row={row} categoryColorMap={categoryColorMap} onImageClick={onImageClick} />
                ))}
                <PaginationBar page={page} total={total} rowsPerPage={rowsPerPage} onPageChange={onPageChange} />
            </Box>
        );
    }

    // Column widths for better readability
    const colWidths: Record<string, string> = {
        image: '56px',
        skuId: '140px',
        skuName: '160px',
        reorderQty: '90px',
        doc: '70px',
        productCategory: '130px',
        skuInventory: '90px',
        gbInventory: '90px',
        bundleInventory: '100px',
        inactiveInStockCount: '100px',
        totalDrr: '80px',
        individualDrr: '80px',
        gbDrr: '60px',
        bundleDrr: '80px',
        gbCount: '200px',
        bundleCount: '200px',
    };

    const headerColumns: [string, string, boolean][] = [
        ['image', 'Image', false],
        ['skuId', 'SKU ID', true],
        ['productCategory', 'Product Category', false],
        ['reorderQty', 'Reorder Qty', true],
        ['doc', 'DOC', true],
        ['skuInventory', 'SKU Inventory', true],
        ['gbInventory', 'Active', true],
        ['inactiveInStockCount', 'Inactive', true],
        ['totalDrr', 'Total DRR', true],
        ['individualDrr', 'Individual DRR', true],
        ['gbDrr', 'Simple DRR', true],
        ['bundleDrr', 'Bundle DRR', true],
        ['gbCount', 'Simple', true],
        ['bundleCount', 'Bundle', true],
    ];

    return (
        <Box>
            {/* Table */}
            <TableContainer component={Paper} variant="outlined" sx={tableContainerSx}>
                <Table size="small" stickyHeader sx={{ minWidth: 1600, ...tableBodySx }}>
                    <TableHead>
                        <TableRow sx={tableHeadRowSx}>
                            {headerColumns.map(([key, label, sortable]) => (
                                <TableCell
                                    key={key}
                                    sx={{
                                        whiteSpace: 'nowrap',
                                        minWidth: key === 'actions' ? 72 : (colWidths[key] || 80),
                                        width: key === 'actions' ? 72 : undefined,
                                        px: key === 'actions' ? 1 : undefined,
                                        position: key === 'image' ? 'sticky' : undefined,
                                        left: key === 'image' ? 0 : undefined,
                                        bgcolor: key === 'image' ? '#FAFAF7' : undefined,
                                        zIndex: key === 'image' ? 3 : undefined,
                                    }}
                                >
                                    {sortable ? (
                                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
                                            <TableSortLabel
                                                active={sortKey === key}
                                                direction={sortDir}
                                                onClick={() => handleSort(key as CompSortKey)}
                                                sx={{ '& .MuiTableSortLabel-icon': { opacity: 1 } }}
                                            >
                                                {(key === 'gbInventory' || key === 'inactiveInStockCount') ? (
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
                                                        <Box component="span" sx={{ fontSize: '0.6rem', fontWeight: 500, color: '#8A8078' }}>Simple Inv.</Box>
                                                        <Box component="span">{label}</Box>
                                                    </Box>
                                                ) : label}
                                            </TableSortLabel>
                                            {key === 'reorderQty' && (
                                                <Tooltip
                                                    title={
                                                        <Box sx={{ fontSize: '0.75rem', lineHeight: 1.6 }}>
                                                            <strong>Reorder Qty = max(ceil(Target DOC × Total DRR − Total Inventory), 0)</strong>
                                                            <br />Target DOC = {targetDoc} days
                                                            <br />Total DRR = Individual DRR + Simple DRR + Bundle DRR
                                                            <br />Total Inventory = SKU Inventory + Simple Inventory (Active)
                                                            <br />If Total DRR is 0, Reorder Qty is 0.
                                                        </Box>
                                                    }
                                                >
                                                    <InfoOutlined sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                                                </Tooltip>
                                            )}
                                            {key === 'doc' && (
                                                <Tooltip
                                                    title={
                                                        <Box sx={{ fontSize: '0.75rem', lineHeight: 1.6 }}>
                                                            <strong>DOC = Total Inventory ÷ Total DRR</strong>
                                                            <br />Days of Cover — how many days current inventory will last at the current sales rate.
                                                            <br />Blank when Total DRR is 0.
                                                        </Box>
                                                    }
                                                >
                                                    <InfoOutlined sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                                                </Tooltip>
                                            )}
                                            {(key === 'gbCount' || key === 'bundleCount') && (
                                                <Tooltip title={key === 'gbCount'
                                                    ? 'Simple (formerly Gift Box) products that include this component, with a count badge and the list of parent SKUs.'
                                                    : 'Bundles that include this component, with a count badge and the list of parent SKUs.'
                                                }>
                                                    <InfoOutlined sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                                                </Tooltip>
                                            )}
                                            {key === 'inactiveInStockCount' && (
                                                <Tooltip title="Count of distinct INACTIVE Simple/Bundle SKUs that use this component and currently hold real stock — a count of SKUs, not a stock quantity. Not counted in Total Inventory/DOC/Reorder Qty.">
                                                    <InfoOutlined sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                                                </Tooltip>
                                            )}
                                        </Box>
                                    ) : (
                                        label
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sorted.map((row) => {
                            const isExpanded = expandedRow === row.skuId;
                            return (
                            <TableRow
                                key={row.skuId}
                                hover
                                sx={{
                                    bgcolor: isExpanded ? 'rgba(220, 88, 41, 0.06)' : (row.productCategory ? 'inherit' : 'rgba(220, 38, 38, 0.04)'),
                                    borderLeft: row.productCategory ? 'none' : '3px solid #dc2626',
                                    '&:hover': { bgcolor: isExpanded ? 'rgba(220, 88, 41, 0.1)' : (row.productCategory ? 'action.hover' : 'rgba(220, 38, 38, 0.08)') },
                                }}
                            >
                                <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'white', zIndex: 2 }}>
                                    <ImageThumb
                                        images={row.images && row.images.length > 0 ? row.images : (row.skuImage && row.skuImage.startsWith('http') ? [row.skuImage] : [])}
                                        label={row.skuId}
                                        onClick={(e) => {
                                            e?.stopPropagation();
                                            const allImages = row.images && row.images.length > 0 ? row.images : (row.skuImage && row.skuImage.startsWith('http') ? [row.skuImage] : []);
                                            if (allImages.length > 0) {
                                                onImageClick(row.skuId, allImages);
                                            }
                                        }}
                                        size={36}
                                    />
                                </TableCell>
                                <TableCell
                                    sx={{
                                        fontFamily: 'monospace',
                                        fontWeight: 600,
                                        maxWidth: 180,
                                    }}
                                >
                                    <Box sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                        {row.skuId}
                                    </Box>
                                    {row.skuName !== row.skuId && (
                                        <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.65rem', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                            {row.skuName}
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell sx={{ maxWidth: 220 }}>
                                    <Tooltip title={row.productCategory || 'No category data in Redshift for this SKU — needs to be added at the source (Plytix) to fix this'}>
                                        <Chip
                                            label={row.productCategory || '⚠️ Blank — needs fix'}
                                            size="small"
                                            variant={row.productCategory ? 'outlined' : 'filled'}
                                            sx={{
                                                fontSize: '0.65rem',
                                                height: 20,
                                                maxWidth: '100%',
                                                '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                                                ...(row.productCategory
                                                    ? (categoryColorMap.get(row.productCategory) || COMPONENT_TYPE_STYLE.SKU.sx)
                                                    : COMPONENT_TYPE_STYLE.BLANK.sx),
                                            }}
                                        />
                                    </Tooltip>
                                </TableCell>
                                <TableCell
                                    sx={{
                                        fontFamily: 'monospace',
                                        fontWeight: row.reorderQty > 0 ? 700 : 400,
                                        color: row.reorderQty > 0 ? 'error.main' : 'text.disabled',
                                    }}
                                >
                                    {formatNumber(row.reorderQty)}
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={formatDecimal(row.doc)}
                                        size="small"
                                        color={docColorComponent(row.doc)}
                                        variant={row.doc !== null ? 'filled' : 'outlined'}
                                        sx={{ fontWeight: 600, minWidth: 48 }}
                                    />
                                </TableCell>
                                <TableCell sx={{ fontFamily: 'monospace' }}>{formatNumber(row.skuInventory)}</TableCell>
                                <TableCell sx={{ fontFamily: 'monospace' }}>{formatNumber(row.gbInventory)}</TableCell>
                                <TableCell sx={{ fontFamily: 'monospace', color: row.inactiveInStockCount > 0 ? 'warning.dark' : 'text.disabled' }}>
                                    {formatNumber(row.inactiveInStockCount)}
                                </TableCell>
                                <TableCell sx={{ fontFamily: 'monospace' }}>{formatDecimal(row.totalDrr)}</TableCell>
                                <TableCell sx={{ fontFamily: 'monospace' }}>{formatDecimal(row.individualDrr)}</TableCell>
                                <TableCell sx={{ fontFamily: 'monospace' }}>{formatDecimal(row.gbDrr)}</TableCell>
                                <TableCell sx={{ fontFamily: 'monospace' }}>{formatDecimal(row.bundleDrr)}</TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                        <Chip
                                            label={row.gbCount}
                                            size="small"
                                            color={row.gbCount > 0 ? 'primary' : 'default'}
                                            variant="filled"
                                            sx={{ fontWeight: 700, height: 20, fontSize: '0.65rem', minWidth: 22 }}
                                        />
                                        <CollapsedChipList items={row.gbList} max={2} color="primary" expanded={isExpanded} onToggle={() => setExpandedRow(isExpanded ? null : row.skuId)} />
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                        <Chip
                                            label={row.bundleCount}
                                            size="small"
                                            color={row.bundleCount > 0 ? 'secondary' : 'default'}
                                            variant="filled"
                                            sx={{ fontWeight: 700, height: 20, fontSize: '0.65rem', minWidth: 22 }}
                                        />
                                        <CollapsedChipList items={row.bundleList} max={2} expanded={isExpanded} onToggle={() => setExpandedRow(isExpanded ? null : row.skuId)} />
                                    </Box>
                                </TableCell>
                            </TableRow>
                        );}
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <PaginationBar page={page} total={total} rowsPerPage={rowsPerPage} onPageChange={onPageChange} />
        </Box>
    );
};

// ── Button style constants ────────────────────────────────────────────────────

const btnOutlinedSx = {
    textTransform: 'none',
    borderColor: '#ECECE4',
    color: '#4A433D',
    whiteSpace: 'nowrap',
    borderRadius: 1.5,
    height: 36,
    px: 2,
    fontWeight: 500,
    fontSize: '0.8125rem',
    transition: 'all 0.15s ease',
    '& .MuiButton-startIcon': { mr: 0.5, ml: -0.25, '& .MuiSvgIcon-root': { fontSize: 17 } },
    '&:hover': { borderColor: '#8A8078', bgcolor: '#FAFAF7' },
};

const btnContainedSx = {
    textTransform: 'none',
    borderRadius: 1.5,
    bgcolor: '#b8451e',
    boxShadow: '0 1px 3px rgba(220, 88, 41, 0.25)',
    whiteSpace: 'nowrap',
    height: 36,
    px: 2.5,
    fontWeight: 500,
    fontSize: '0.8125rem',
    transition: 'all 0.15s ease',
    '& .MuiButton-startIcon': { mr: 0.5, ml: -0.25, '& .MuiSvgIcon-root': { fontSize: 17 } },
    '&:hover': { bgcolor: '#6d28d9', boxShadow: '0 2px 6px rgba(220, 88, 41, 0.35)' },
};

// ── Main Page ─────────────────────────────────────────────────────────────────

const BundleToolTables: React.FC = () => {
    // Import (Bulk Upload) follows the bundle-skus edit capability — the same permission the
    // backend bulk-upload routes enforce — so Catalog Manager/Member (bundle-skus:edit) can
    // import, not just admins. Was previously hardcoded to role === 'admin'.
    const canEditBundleSkus = usePermission('bundle-skus').can('edit');
    // Creating a new bundle category is gated on add-bundle:edit (not bundle-skus), so
    // catalog roles that hold add-bundle can create categories. Bulk Upload stays on
    // bundle-skus:edit. The backend enforces add-bundle:edit on POST /api/bundle-categories.
    const canAddBundle = usePermission('add-bundle').can('edit');

    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxSections, setLightboxSections] = useState<LightboxSection[]>([]);
    const [searchParams, setSearchParams] = useSearchParams();

    // Tab state from URL: ?view=bundle | component | data-health | (none → Bundle Categories view)
    // A bare ?parent=<SKU> deep-link (no explicit view) lands on the bundle view so the
    // parent's BundleCategoryDetail opens instead of the default Bundle Categories tab.
    const viewParam = searchParams.get('view');
    const hasParentDeepLink = !!searchParams.get('parent');
    const tabValue = viewParam === 'bundle' ? 1 : viewParam === 'component' ? 2 : viewParam === 'data-health' ? 3 : (hasParentDeepLink ? 1 : 0);

    // Restore page + sort + search from URL on initial load
    const initBundlePage = parseInt(searchParams.get('bPage') || '0', 10) || 0;
    const initCompPage   = parseInt(searchParams.get('cPage') || '0', 10) || 0;
    const initSearch     = searchParams.get('q') || '';
    const initBundleSort = searchParams.get('bSort') || 'drr-desc';
    const initCompSort   = searchParams.get('cSort') || 'drr-high';
    const [detailedView, setDetailedView] = useState(searchParams.get('bDetail') === '1');

    const [bundleRows, setBundleRows] = useState<BundleCategoryRow[]>([]);
    const [bundleLoading, setBundleLoading] = useState(true);
    const [bundleError, setBundleError] = useState<string | null>(null);
    const [bundlePage, setBundlePage] = useState(initBundlePage);
    const [bundleTotal, setBundleTotal] = useState(0);

    const [compRows, setCompRows] = useState<ComponentViewRow[]>([]);
    const [compLoading, setCompLoading] = useState(false);
    const [compError, setCompError] = useState<string | null>(null);
    const [compPage, setCompPage] = useState(initCompPage);
    const [compTotal, setCompTotal] = useState(0);
    const [targetDoc, setTargetDoc] = useState(60);
    const [blankCategoryCount, setBlankCategoryCount] = useState(0);

    const [compSortByString, setCompSortByString] = useState<string>(initCompSort);
    const [bundleSortByString, setBundleSortByString] = useState<string>(initBundleSort);
    const [exportingComponentView, setExportingComponentView] = useState(false);

    // Filter state for Bundle View (matching BundleCategoryManager pattern)
    // Defaults to 'active' so landing on this tab shows only active bundles.
    const [statusFilter, setStatusFilter] = useState<string>('active');
    const [brandFilter, setBrandFilter] = useState<string>('all');
    const [categoryFilter, setCategoryFilter] = useState<string>('all');
    const [tagFilter, setTagFilter] = useState<string>('all');
    const [saleTypeFilter, setSaleTypeFilter] = useState<string>('all');
    const [saleTagFilter, setSaleTagFilter] = useState<string>('Live');
    const [needsReorderFilter, setNeedsReorderFilter] = useState<string>('all');

    // Filter state for Component View (computed after $group)
    const [productCategoryFilter, setProductCategoryFilter] = useState<string>('all');
    const [categoryOptions, setCategoryOptions] = useState<string[]>([]);
    const [usedInGbFilter, setUsedInGbFilter] = useState<string>('all');
    const [usedInBundleFilter, setUsedInBundleFilter] = useState<string>('all');
    const [docRangeFilter, setDocRangeFilter] = useState<DocRangeValue>(EMPTY_DOC_RANGE);
    const [bundleDocFilter, setBundleDocFilter] = useState<DocRangeValue>(EMPTY_DOC_RANGE);
    const [bundleCogsFilter, setBundleCogsFilter] = useState<CogsRangeValue>(EMPTY_COGS_RANGE);

    // Export/Bulk Upload/New Category/Refresh only apply to the Bundle Categories tab now
    // (it owns those dialogs internally) — this ref drives them from the shared header above.
    const bundleCategoryManagerRef = useRef<BundleCategoryManagerHandle>(null);

    const [searchQuery, setSearchQuery] = useState(initSearch);
    const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Which parent's version detail is open. Seeded from the ?parent=<SKU> deep-link so an
    // actionable's "Open parent" lands straight on that parent's BundleCategoryDetail.
    const [selectedBundleSKU, setSelectedBundleSKU] = useState<string | null>(() => searchParams.get('parent'));

    // ── Sync state → URL on every relevant change ───────────────────────
    // IMPORTANT: this effect is the single writer of the URL, so it MUST carry every param we
    // want to survive a mount — including `parent`. When a parent detail is open we always write
    // `view=bundle&parent=<SKU>`; otherwise `parent` is dropped. Without this, a bare
    // ?parent=<SKU> deep-link was stripped on mount and the page fell back to the list.
    useEffect(() => {
        const params: Record<string, string> = {};
        if (selectedBundleSKU) {
            // A parent detail is open — pin the bundle view and keep the parent in the URL so a
            // refresh or shared link reopens the same detail.
            params.view = 'bundle';
            params.parent = selectedBundleSKU;
        } else if (viewParam) {
            params.view = viewParam;
        }
        if (searchQuery) params.q = searchQuery;
        if (tabValue === 1 && !selectedBundleSKU) {
            if (bundlePage > 0) params.bPage = String(bundlePage);
            if (bundleSortByString !== 'drr-desc') params.bSort = bundleSortByString;
            if (detailedView) params.bDetail = '1';
        }
        if (tabValue === 2) {
            if (compPage > 0) params.cPage = String(compPage);
            if (compSortByString !== 'drr-high') params.cSort = compSortByString;
        }
        setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewParam, searchQuery, bundlePage, bundleSortByString, compPage, compSortByString, tabValue, detailedView, selectedBundleSKU]);
    const toast = useToast();

    // Filter values fetched directly from the DB (not from current page of results)
    const [filterOptions, setFilterOptions] = useState<{
        brands: string[];
        categories: string[];
        subCategories: string[];
        tags: string[];
    }>({ brands: [], categories: [], subCategories: [], tags: [] });

    // Fetch distinct filter values from the backend on mount
    useEffect(() => {
        WarehouseAPI.getBundleCategoryFilterValues().then(vals => {
            if (vals) setFilterOptions(vals);
        }).catch(() => {});
    }, []);

    // Distinct real Product Category values (e.g. "Jewelry", "Packaging", "Rakhi") for
    // Component View's filter dropdown — reflects actual data instead of a static list.
    useEffect(() => {
        WarehouseAPI.getComponentCategoryOptions().then(setCategoryOptions).catch(() => {});
    }, []);

    // When any dropdown filter changes: reset page to 0 and fetch immediately
    // (search is handled separately in the debounce effect below)
    useEffect(() => {
        if (tabValue === 1) {
            setBundlePage(0);
            fetchBundleView(0);
        } else if (tabValue === 2) {
            setCompPage(0);
            fetchComponentView(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter, brandFilter, categoryFilter, tagFilter, saleTypeFilter, saleTagFilter, needsReorderFilter, productCategoryFilter, usedInGbFilter, usedInBundleFilter, docRangeFilter, bundleDocFilter, bundleCogsFilter]);

    // ── Fetch bundle view data ──────────────────────────────────────────
    const fetchBundleView = useCallback(async (pageNum?: number, sortBy?: string) => {
        const p = pageNum !== undefined ? pageNum : bundlePage;
        const sb = sortBy || bundleSortByString;
        setBundleLoading(true);
        setBundleError(null);
        try {
            const filters = {
                statusFilter,
                brandFilter,
                categoryFilter,
                tagFilter,
                saleTypeFilter,
                saleTagFilter,
                needsReorderFilter,
                ...docRangeParams(bundleDocFilter, true),
                ...cogsRangeParams(bundleCogsFilter),
                // One row per bundle version (so inactive versions of a parent each show
                // as their own row) instead of Bundle Categories tab's collapsed shape.
                expandVersions: 'true',
            };
            const response = await WarehouseAPI.getBundleCategories(p, PAGE_SIZE, searchQuery, sb, filters);
            setBundleRows(response.data as BundleCategoryRow[]);
            setBundleTotal(response.total);
        } catch (err: any) {
            setBundleError(err.message || 'Failed to fetch bundle data');
        } finally {
            setBundleLoading(false);
        }
    }, [bundlePage, searchQuery, statusFilter, brandFilter, categoryFilter, tagFilter, saleTypeFilter, saleTagFilter, needsReorderFilter, bundleDocFilter, bundleCogsFilter, bundleSortByString]);

    // ── Fetch component view data (with search) ─────────────────────────
    const fetchComponentView = useCallback(async (pageNum?: number, sortBy?: string) => {
        const p = pageNum !== undefined ? pageNum : compPage;
        const sb = sortBy || compSortByString;
        setCompLoading(true);
        setCompError(null);
        try {
            const response = await WarehouseAPI.getComponentView(p, PAGE_SIZE, searchQuery, sb, {
                productCategoryFilter,
                usedInGbFilter,
                usedInBundleFilter,
                ...docRangeParams(docRangeFilter, false),
            });
            setCompRows(response.data.rows);
            setCompTotal(response.data.total ?? response.data.rows.length);
            setTargetDoc(response.data.targetDoc);
            setBlankCategoryCount(response.data.blankCategoryCount ?? 0);
        } catch (err: any) {
            setCompError(err.message || 'Failed to fetch component data');
        } finally {
            setCompLoading(false);
        }
    }, [compPage, searchQuery, compSortByString, productCategoryFilter, usedInGbFilter, usedInBundleFilter, docRangeFilter]);

    // Fetch on mount is handled by the page-change effects below (bundlePage/compPage start at 0)

    // Debounced search: reset to page 0 and re-fetch when searchQuery changes
    useEffect(() => {
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        searchTimerRef.current = setTimeout(() => {
            searchTimerRef.current = null;
            if (tabValue === 1) {
                setBundlePage(0);
                fetchBundleView(0);
            } else if (tabValue === 2) {
                setCompPage(0);
                fetchComponentView(0);
            }
        }, 400);
        return () => {
            if (searchTimerRef.current) {
                clearTimeout(searchTimerRef.current);
                searchTimerRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery]);

    // Re-fetch when page changes (fires on mount too when page is 0, which is redundant
    // but harmless — the duplicate request will be served from browser cache)
    useEffect(() => { if (tabValue === 1) fetchBundleView(); }, [bundlePage]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { if (tabValue === 2) fetchComponentView(); }, [compPage]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleBundlePageChange = (page: number) => {
        setBundlePage(page);
    };

    const handleCompPageChange = (page: number) => {
        setCompPage(page);
    };

    const handleBundleSortChange = (key: BundleSortKey) => {
        const [prevKey, prevDir] = bundleSortByString.split('-');
        const nextDir: 'asc' | 'desc' = prevKey === key && prevDir === 'asc' ? 'desc' : 'asc';
        const nextSortBy = `${key}-${nextDir}`;
        setBundleSortByString(nextSortBy);
        setBundlePage(0);
        fetchBundleView(0, nextSortBy);
    };

    const handleCompSortChange = (key: CompSortKey) => {
        const current = parseCompSortBy(compSortByString);
        const nextDir: 'asc' | 'desc' = current.key === key ? (current.dir === 'asc' ? 'desc' : 'asc') : 'asc';
        const nextSortBy = buildCompSortBy(key, nextDir);
        setCompSortByString(nextSortBy);
        setCompPage(0);
        fetchComponentView(0, nextSortBy);
    };

    const handleTabChange = (_: any, newVal: number) => {
        setSelectedBundleSKU(null);
        if (newVal === 0) {
            setSearchParams({});
            setSearchQuery('');
            return;
        }
        if (newVal === 3) {
            setSearchParams({ view: 'data-health' });
            return;
        }
        const viewP = newVal === 1 ? 'bundle' : 'component';
        setSearchQuery('');
        setBundlePage(0);
        setCompPage(0);
        setSearchParams({ view: viewP });
        if (newVal === 1) {
            fetchBundleView(0);
        } else {
            fetchComponentView(0);
        }
    };

    // ── Key to force remount BundleCategoryManager on tab switch ──────
    const categoryManagerKey = `cat-mgr-${tabValue}`;

    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key !== 'Enter') return;
        // Cancel the debounced search and trigger the search immediately
        if (searchTimerRef.current) {
            clearTimeout(searchTimerRef.current);
            searchTimerRef.current = null;
        }
        if (tabValue === 1) {
            setBundlePage(0);
            fetchBundleView(0);
        } else if (tabValue === 2) {
            setCompPage(0);
            fetchComponentView(0);
        }
    };

    const handleRowClick = (parentSKU: string) => {
        setSelectedBundleSKU(parentSKU);
    };

    const handleCloseDetail = () => {
        // Clearing the selection is enough: the URL-sync effect is the single writer and will
        // drop ?parent= and land the URL on ?view=bundle, so returning shows the bundle list.
        setSelectedBundleSKU(null);
    };

    // ── CSV Export ──────────────────────────────────────────────────────
    // Every cell MUST go through this — formatNumber uses toLocaleString('en-IN'), which
    // inserts thousands-separator commas (e.g. "1,235"), and free-text fields like image
    // filenames routinely contain literal commas too (e.g. "Photo Feb 10, 2026.png").
    // Either one silently splits a row into extra columns if left unquoted.
    // (csvCell lives in ../utils/csv — shared with the Bundle Categories export.)

    const [exportingBundleView, setExportingBundleView] = useState(false);

    // Same pattern as handleExportComponentView below — fetch ALL rows matching the
    // current search/filters/sort (limit=0) rather than just the loaded page.
    const handleExportBundleView = async () => {
        setExportingBundleView(true);
        try {
            const detailed = detailedView;
            const response = await WarehouseAPI.getBundleCategories(0, 0, searchQuery, bundleSortByString, {
                statusFilter,
                brandFilter,
                categoryFilter,
                tagFilter,
                saleTypeFilter,
                saleTagFilter,
                needsReorderFilter,
                ...docRangeParams(bundleDocFilter, true),
                ...cogsRangeParams(bundleCogsFilter),
                expandVersions: 'true',
                // CSV export never renders images — skip the Plytix enrichment lookup
                // entirely so a large unpaginated export doesn't pay for it.
                skipImageEnrich: 'true',
                includeImages: 'true',
                // Detailed already enriches COGS (+ per-child cogs/cogsPct) via includeProcurement;
                // the plain export has no procurement pass, so ask for COGS explicitly so b.cogs is
                // populated for the bundle-level COGS column.
                ...(detailed ? { includeProcurement: 'true' } : { includeCogs: 'true' }),
            });
            const rows = (response.data as BundleCategoryRow[]).filter(r => r.activeBundle);

            // Plain numbers (no thousands separators, no dashes) so every numeric column opens
            // as a number in Excel/Sheets and can be summed, sorted and pivoted.
            const n = (v: unknown, dp?: number): string | number => {
                if (v === null || v === undefined || v === '' || Number.isNaN(Number(v))) return '';
                return dp === undefined ? Number(v) : Number(Number(v).toFixed(dp));
            };
            // The bundle's Plytix images in catalogue order — the export uses the first. The legacy
            // per-bundle imageURL is only a fallback and only when it is a real http URL — it holds
            // junk like "FALSE" on some bundles.
            const imageUrls = (b: any): string[] => {
                const fromPlytix: string[] = Array.isArray(b.images) ? b.images.filter((u: string) => /^https?:\/\//.test(u)) : [];
                if (fromPlytix.length > 0) return fromPlytix;
                return typeof b.imageURL === 'string' && /^https?:\/\//.test(b.imageURL) ? [b.imageURL] : [];
            };
            const typeLabel = (t?: string) => (t === 'ADD_ON' ? 'Add-On' : t === 'PACKAGING' ? 'Packaging' : 'SKU');
            const stageValues = (p?: { grnPending: number; inTransit: number; ordered: number; poNotPlaced: number } | null) =>
                p ? [p.grnPending, p.inTransit, p.ordered, p.poNotPlaced] : ['', '', '', ''];

            const bundleCols = ['Parent SKU', 'SKU ID', 'Status', 'SKU Name', 'Type', 'Image URL', 'Physical DOC', 'Potential DOC', 'DRR (7-day)', 'Physical Inventory', 'Can Be Made', 'COGS (post-GST)'];
            const stageCols = ['Delivered (GRN Pending)', 'In Transit', 'Ordered', 'Reorder Not Placed'];

            const bundleCells = (cat: BundleCategoryRow) => {
                const b = cat.activeBundle!;
                return [
                    cat.parentSKU,
                    b.bundleSku,
                    (b as any).isActive === false ? 'Inactive' : 'Active',
                    b.name || '',
                    (b as any).saleType === 'Simple' ? 'Simple' : 'Bundle',
                    imageUrls(b)[0] || '',
                    n((b as any).physicalDoc, 1),
                    n((b as any).potentialDoc, 1),
                    n(b.drr, 2),
                    n(b.inventory),
                    n(b.canBeMade),
                    n((b as any).cogs?.total),
                ];
            };

            const csvRows: string[] = [];
            if (!detailed) {
                // Simple: one row per bundle SKU; its components listed per type in one cell each.
                csvRows.push([...bundleCols, 'Components Costed', 'Components Total', 'SKU Components', 'Add-Ons', 'Packaging Components'].join(','));
                rows.forEach(cat => {
                    const b = cat.activeBundle as any;
                    const componentsList = b.componentsList || [];
                    const byType = (typeKey: 'SKU' | 'ADD_ON' | 'PACKAGING') =>
                        componentsList.filter((c: any) => (c.componentType || 'SKU') === typeKey).map((c: any) => c.sku).join(' | ');
                    csvRows.push([...bundleCells(cat), n(b.cogs?.costedCount), n(b.cogs?.componentCount), byType('SKU'), byType('ADD_ON'), byType('PACKAGING')].map(csvCell).join(','));
                });
            } else {
                // Detailed: one row per bundle-child pair, bundle columns repeated on every row so the
                // sheet filters/pivots cleanly. A bundle with no children still gets one row.
                csvRows.push([
                    ...bundleCols,
                    'Child SKU', 'Child Type', 'Child Inventory', 'Limits Can Be Made', ...stageCols.map(c => `Child ${c}`),
                    'Child COGS', 'Child COGS Share %',
                ].join(','));
                rows.forEach(cat => {
                    const b = cat.activeBundle!;
                    const base = bundleCells(cat);
                    const children: Array<{ sku: string; componentType?: string; inventory?: number; isLimiting?: boolean; procurement?: any; cogs?: number | null; cogsPct?: number | null }> =
                        [...((b as any).componentsList || [])].sort((x: any, y: any) => (x.inventory ?? Infinity) - (y.inventory ?? Infinity));
                    if (children.length === 0) {
                        csvRows.push([...base, '', '', '', '', '', '', '', '', '', ''].map(csvCell).join(','));
                        return;
                    }
                    children.forEach(c => {
                        csvRows.push([
                            ...base,
                            c.sku, typeLabel(c.componentType), n(c.inventory), c.isLimiting ? 'Yes' : 'No',
                            ...stageValues(c.procurement),
                            n(c.cogs), n(c.cogsPct),
                        ].map(csvCell).join(','));
                    });
                });
            }
            downloadCsv(csvRows, detailed ? 'view-detailed' : 'view');
            toast.success(`Exported ${csvRows.length - 1} row(s) to CSV${detailed ? ' (detailed: one row per child)' : ''}`, { duration: 3000 });
        } catch (err: any) {
            toast.error(err.message || 'Failed to export bundle view', { duration: 3000 });
        } finally {
            setExportingBundleView(false);
        }
    };

    const handleExportComponentView = async () => {
        setExportingComponentView(true);
        try {
            const response = await WarehouseAPI.getComponentView(0, 0, searchQuery, compSortByString, {
                productCategoryFilter,
                usedInGbFilter,
                usedInBundleFilter,
                ...docRangeParams(docRangeFilter, false),
                // CSV export never renders images — skip the Plytix enrichment lookup
                // entirely so a large unpaginated export doesn't pay for it.
                skipImageEnrich: true,
            });
            const rows = response.data.rows;
            const headers = [
                'SKU ID', 'SKU Name', 'Product Category', 'Is Packaging', 'Reorder Qty', 'DOC',
                'SKU Inventory', 'Simple Inventory (Active)', 'Simple Inventory (Inactive)',
                'Total DRR', 'Individual DRR', 'Simple DRR', 'Bundle DRR',
                'Simple Count', 'Simple SKUs', 'Bundle Count', 'Bundle SKUs',
            ];
            const csvRows = [headers.join(',')];
            rows.forEach(row => {
                csvRows.push([
                    row.skuId,
                    row.skuName,
                    row.productCategory || '',
                    row.isPackaging ? 'Yes' : 'No',
                    row.reorderQty,
                    formatDecimal(row.doc),
                    row.skuInventory,
                    row.gbInventory,
                    row.inactiveInStockCount,
                    formatDecimal(row.totalDrr),
                    formatDecimal(row.individualDrr),
                    formatDecimal(row.gbDrr),
                    formatDecimal(row.bundleDrr),
                    row.gbCount,
                    row.gbList.join(';'),
                    row.bundleCount,
                    row.bundleList.join(';'),
                ].map(csvCell).join(','));
            });
            downloadCsv(csvRows, 'component-view');
            toast.success(`Exported ${rows.length} row(s) to CSV`, { duration: 3000 });
        } catch (err: any) {
            toast.error(err.message || 'Failed to export component view', { duration: 3000 });
        } finally {
            setExportingComponentView(false);
        }
    };

    const downloadCsv = (csvRows: string[], suffix: string) => {
        const csvContent = csvRows.join('\n');
        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `bundle-${suffix}-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    return (
        <PageContainer>
            <PageHeader
                icon={<CategoryIcon />}
                title="Bundle Catalog"
                subtitle="Bundle categories, Simple/Bundle and component stock views, and data health"
            />
            {/* Dynamic header based on active tab */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    sx={{ flexShrink: 0 }}
                >
                    <Tab icon={<CategoryIcon sx={{ fontSize: 18 }} />} label="Bundle Categories" iconPosition="start" />
                    <Tab icon={<Inventory2 sx={{ fontSize: 18 }} />} label="Simple / Bundle view" iconPosition="start" />
                    <Tab icon={<Extension sx={{ fontSize: 18 }} />} label="Component view" iconPosition="start" />
                    <Tab icon={<MonitorHeart sx={{ fontSize: 18 }} />} label="Data Health" iconPosition="start" />
                </Tabs>
                {tabValue === 0 && (
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Download />}
                            onClick={() => bundleCategoryManagerRef.current?.exportData()}
                            sx={btnOutlinedSx}
                        >
                            Export
                        </Button>
                        {canEditBundleSkus && (
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<Upload />}
                                onClick={() => bundleCategoryManagerRef.current?.openBulkUploadDialog()}
                                sx={btnOutlinedSx}
                            >
                                Bulk Upload
                            </Button>
                        )}
                        {canAddBundle && (
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<Add />}
                                onClick={() => bundleCategoryManagerRef.current?.openCreateDialog()}
                                sx={btnContainedSx}
                            >
                                New Category
                            </Button>
                        )}
                        <IconButton size="small" color="default" onClick={() => bundleCategoryManagerRef.current?.refresh()} title="Refresh">
                            <Refresh fontSize="small" />
                        </IconButton>
                    </Box>
                )}
                {tabValue === 1 && (
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        <Tooltip title="Show each bundle's children on their own rows, with stock and procurement quantities">
                            <FormControlLabel
                                sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: '0.8rem', fontWeight: 700 } }}
                                control={
                                    <Switch
                                        size="small"
                                        checked={detailedView}
                                        onChange={(e) => setDetailedView(e.target.checked)}
                                        inputProps={{ 'aria-label': 'Detailed view' }}
                                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#DC5829' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#DC5829' } }}
                                    />
                                }
                                label="Detailed view"
                            />
                        </Tooltip>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={exportingBundleView ? <CircularProgress size={14} /> : <Download />}
                            onClick={handleExportBundleView}
                            disabled={exportingBundleView}
                            sx={btnOutlinedSx}
                        >
                            {exportingBundleView ? 'Exporting…' : detailedView ? 'Export Detailed CSV' : 'Export CSV'}
                        </Button>
                    </Box>
                )}
                {tabValue === 2 && (
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={exportingComponentView ? <CircularProgress size={14} /> : <Download />}
                            onClick={handleExportComponentView}
                            disabled={exportingComponentView}
                            sx={btnOutlinedSx}
                        >
                            {exportingComponentView ? 'Exporting…' : 'Export CSV'}
                        </Button>
                    </Box>
                )}
            </Box>


            {/* Search + Filters — a flex row that wraps to a second line only on narrow
                viewports (no horizontal scroll). The shortened "All" labels keep each pill
                tight so the row fits on a normal-width screen. The row sits flush at the
                container's left edge, lining the search field up under the tab labels.
                Shared by the Simple/Bundle (tabValue 1) and Component (tabValue 2) tabs. */}
            {tabValue > 0 && tabValue < 3 && (
                <Box sx={{ mb: 1.5, display: 'flex', gap: 1.25, alignItems: 'center', flexWrap: 'wrap' }}>
                    {/* Search */}
                    <TextField
                        size="small"
                        placeholder={tabValue === 1 ? "Search by SKU, name, or child…" : "Search by SKU ID or name…"}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={handleSearchKeyDown}
                        sx={{
                            flex: '0 0 auto',
                            minWidth: 200,
                            maxWidth: 340,
                            '& .MuiOutlinedInput-root': {
                                height: 40,
                                bgcolor: '#ffffff',
                                '& fieldset': { borderColor: '#ECECE4' },
                                '&:hover fieldset': { borderColor: '#DC5829' },
                                '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                            },
                            '& .MuiInputBase-input': { color: '#1A1410' },
                        }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><Search sx={{ color: 'text.secondary', fontSize: 18 }} /></InputAdornment>,
                        }}
                    />

                    {/* Bundle View filters */}
                    {tabValue === 1 && (
                        <>
                            <FilterDropdown
                                label="Status"
                                value={statusFilter}
                                onChange={setStatusFilter}
                                allLabel="All"
                                minWidth={90}
                                options={[
                                    { value: 'active', label: 'Active' },
                                    { value: 'inactive', label: 'Inactive' },
                                    { value: 'future', label: 'Has Future Version' },
                                ]}
                            />
                            <FilterDropdown
                                label="Sale Tag"
                                value={saleTagFilter}
                                onChange={setSaleTagFilter}
                                allLabel="All"
                                minWidth={90}
                                options={[
                                    { value: 'Live', label: 'Live' },
                                    { value: 'Sunset', label: 'Sunset' },
                                ]}
                            />
                            <FilterDropdown
                                label="Brand"
                                value={brandFilter}
                                onChange={setBrandFilter}
                                allLabel="All"
                                minWidth={90}
                                options={[
                                    ...filterOptions.brands.map(brand => ({ value: brand, label: brand })),
                                    { value: 'BLANK', label: 'Blank' },
                                ]}
                            />
                            <FilterDropdown
                                label="Category"
                                value={categoryFilter}
                                onChange={setCategoryFilter}
                                allLabel="All"
                                minWidth={90}
                                options={filterOptions.categories.map(cat => ({ value: cat, label: cat }))}
                            />
                            <FilterDropdown
                                label="Tags"
                                value={tagFilter}
                                onChange={setTagFilter}
                                allLabel="All"
                                minWidth={90}
                                options={filterOptions.tags.map(tag => ({ value: tag, label: tag }))}
                            />
                            <FilterDropdown
                                label="Sale Type"
                                value={saleTypeFilter}
                                onChange={setSaleTypeFilter}
                                allLabel="All"
                                minWidth={90}
                                options={[
                                    { value: 'Simple', label: 'Simple' },
                                    { value: 'Bundle', label: 'Bundle' },
                                ]}
                            />
                            <FilterDropdown
                                label="Primary Tag"
                                value={needsReorderFilter}
                                onChange={setNeedsReorderFilter}
                                allLabel="All"
                                minWidth={110}
                                options={[
                                    { value: 'true', label: 'Primary' },
                                    { value: 'false', label: 'Non Primary' },
                                    { value: 'unmapped', label: 'Unmapped' },
                                ]}
                            />
                            <DocRangeFilter value={bundleDocFilter} onChange={setBundleDocFilter} showBasis />
                            <CogsRangeFilter value={bundleCogsFilter} onChange={setBundleCogsFilter} />
                        </>
                    )}

                    {/* Component View filters */}
                    {tabValue === 2 && (
                        <>
                            <FilterDropdown
                                label="Product Category"
                                value={productCategoryFilter}
                                onChange={setProductCategoryFilter}
                                allLabel="All"
                                minWidth={110}
                                options={[
                                    ...categoryOptions.map(cat => ({ value: cat, label: cat })),
                                    { value: 'BLANK', label: 'Blank' },
                                ]}
                            />
                            <FilterDropdown
                                label="Used in Simple"
                                value={usedInGbFilter}
                                onChange={setUsedInGbFilter}
                                allLabel="All"
                                minWidth={90}
                                options={[
                                    { value: 'yes', label: 'Yes' },
                                    { value: 'no', label: 'No' },
                                ]}
                            />
                            <FilterDropdown
                                label="Used in Bundle"
                                value={usedInBundleFilter}
                                onChange={setUsedInBundleFilter}
                                allLabel="All"
                                minWidth={90}
                                options={[
                                    { value: 'yes', label: 'Yes' },
                                    { value: 'no', label: 'No' },
                                ]}
                            />
                            <DocRangeFilter value={docRangeFilter} onChange={setDocRangeFilter} />
                        </>
                    )}

                    {/* Clear All */}
                    {((tabValue === 1 && (statusFilter !== 'all' || brandFilter !== 'all' || categoryFilter !== 'all' || tagFilter !== 'all' || saleTypeFilter !== 'all' || saleTagFilter !== 'all' || needsReorderFilter !== 'all' || isDocRangeActive(bundleDocFilter) || isCogsRangeActive(bundleCogsFilter))) ||
                      (tabValue === 2 && (productCategoryFilter !== 'all' || usedInGbFilter !== 'all' || usedInBundleFilter !== 'all' || isDocRangeActive(docRangeFilter)))) && (
                        <Button
                            size="small"
                            onClick={() => {
                                setStatusFilter('all'); setBrandFilter('all'); setCategoryFilter('all');
                                setTagFilter('all'); setSaleTypeFilter('all');
                                setSaleTagFilter('all'); setNeedsReorderFilter('all'); setProductCategoryFilter('all'); setUsedInGbFilter('all');
                                setUsedInBundleFilter('all'); setDocRangeFilter(EMPTY_DOC_RANGE); setBundleDocFilter(EMPTY_DOC_RANGE); setBundleCogsFilter(EMPTY_COGS_RANGE);
                            }}
                            sx={{ height: 40, color: 'text.secondary', '&:hover': { color: 'primary.main' }, textTransform: 'none', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                        >
                            Clear All
                        </Button>
                    )}
                </Box>
            )}

            {/* Table Content / Version Detail */}
            {tabValue === 0 ? (
                <Box key={categoryManagerKey}>
                    <BundleCategoryManager embedded ref={bundleCategoryManagerRef} />
                </Box>
            ) : tabValue === 3 ? (
                <DataHealthTab />
            ) : selectedBundleSKU ? (
                <BundleCategoryDetail
                    parentSKU={selectedBundleSKU}
                    onClose={handleCloseDetail}
                    onUpdate={fetchBundleView}
                />
            ) : tabValue === 1 ? (
                <BundleTableView
                    rows={bundleRows}
                    loading={bundleLoading}
                    error={bundleError}
                    onRetry={() => fetchBundleView(bundlePage)}
                    onRowClick={handleRowClick}
                    page={bundlePage}
                    total={bundleTotal}
                    rowsPerPage={PAGE_SIZE}
                    onPageChange={handleBundlePageChange}
                    sortBy={bundleSortByString}
                    onSortChange={handleBundleSortChange}
                    detailed={detailedView}
                    onImageClick={(title, images) => {
                        setLightboxSections([{ title, images }]);
                        setLightboxOpen(true);
                    }}
                />
            ) : (
                <>
                {blankCategoryCount > 0 && (
                    <Alert
                        severity="error"
                        sx={{ mb: 1.5, alignItems: 'center' }}
                        action={
                            <Button
                                size="small"
                                color="error"
                                variant="outlined"
                                onClick={() => setProductCategoryFilter('BLANK')}
                                sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                            >
                                Show these SKUs
                            </Button>
                        }
                    >
                        <strong>{blankCategoryCount}</strong> component{blankCategoryCount === 1 ? '' : 's'} {blankCategoryCount === 1 ? 'has' : 'have'} no Product Category data from Redshift — these need to be categorized at the source (Plytix) to show up correctly here.
                    </Alert>
                )}
                <ComponentViewTable
                    rows={compRows}
                    loading={compLoading}
                    error={compError}
                    onRetry={() => fetchComponentView(compPage)}
                    targetDoc={targetDoc}
                    page={compPage}
                    total={compTotal}
                    rowsPerPage={PAGE_SIZE}
                    onPageChange={handleCompPageChange}
                    sortBy={compSortByString}
                    onSortChange={handleCompSortChange}
                    onImageClick={(title, images) => {
                        setLightboxSections([{ title, images }]);
                        setLightboxOpen(true);
                    }}
                    categoryOptions={categoryOptions}
                />
                </>
            )}

            <ImageLightbox
                open={lightboxOpen}
                onClose={() => setLightboxOpen(false)}
                sections={lightboxSections}
            />
        </PageContainer>
    );
};

export default BundleToolTables;
